/**
 * batch-70 voertuig-meldingen (FO-527, spec 227) - de pure regels van de
 * voertuigmutatie-meldingen. Geen database, geen klok (de aanroeper geeft `nu`
 * mee): volledig unit-getest.
 *
 *  - `bepaalVoertuigMeldingen(event, voertuig, instellingen, nu)`: per kanaal
 *    (WASSINK, CARTRACKER, NAW) precies één besluit: melden (met geplande tijd,
 *    terugwerkend-markering en eventueel een akkoord-eis) of overslaan met een
 *    reden. Nooit stil: overslaan heeft altijd een reden, en een overslaan dat
 *    op een fout wijst (leasemaatschappij onbekend) vraagt een signaal.
 *  - `isNawRelevant(event, voertuig, instellingen)`: verandert de loonstrook?
 *  - `bundelEvents(events, venster)`: events op dezelfde bundel-sleutel binnen
 *    het venster (vast vanaf het eerste event) worden één melding.
 *  - `bundelUitkomst(events)`: de eindstand van een bundel, en of de mutaties
 *    elkaar opheffen (toewijzen en direct weer afmelden).
 *  - onderwerpen en het herkennen van een antwoord, plus de bevestigingstermijn.
 */
import type {
  VoertuigMeldingKanaal,
  VoertuigMutatieBron,
  VoertuigMutatieSoort,
  VoertuigType,
} from '@streep/types'
import { werkdagenSinds } from './besteladvies'
import { dagenTussen } from './inkoop-doorbelasting'

const MINUUT_MS = 60 * 1000

export type VoertuigEigendomSoort = 'KOOP' | 'FINANCIAL_LEASE' | 'OPERATIONAL_LEASE' | 'HUUR' | 'LEEN'

/** Een event zoals de regels het nodig hebben (de API normaliseert de JSON). */
export interface VoertuigMeldingEventInvoer {
  soort: VoertuigMutatieSoort
  bron: VoertuigMutatieBron
  ingangsdatum: Date
  createdAt: Date
  voertuigId: string
  employeeId: string | null
  /** Bestuurder-events: de soort toewijzing. */
  toewijzingSoort?: 'VAST' | 'TIJDELIJK' | null
  /** Tijdelijke bestuurder: begin en (verwacht of werkelijk) einde. */
  tijdelijkVan?: Date | null
  tijdelijkTot?: Date | null
  /** TIJDELIJK_BEEINDIGD: is de start destijds wel aan de salarisverwerker gemeld? */
  tijdelijkStartGemeld?: boolean
  /** Verklaring: het bijtellingsgevolg nu en daarvoor (null = onbekend/geen). */
  bijtellingNu?: boolean | null
  bijtellingVoorheen?: boolean | null
  /** VERKLARING_INGETROKKEN: WISSEL | VERVANGEN | CORRECTIE, plus het type van de ingetrokken verklaring. */
  intrekReden?: string | null
  verklaringType?: 'UITSLUITEND_ZAKELIJK' | 'OOK_PRIVE' | null
}

export interface VoertuigMeldingVoertuigInvoer {
  eigendom: VoertuigEigendomSoort
  type: VoertuigType
  leaseMaatschappijId: string | null
  leaseMaatschappijNaam: string | null
}

export interface VoertuigMeldingRegels {
  wassinkActief: boolean
  wassinkVertragingMin: number
  wassinkLeveranciers: readonly string[]
  cartrackerActief: boolean
  cartrackerVertragingMin: number
  cartrackerVoertuigTypes: readonly VoertuigType[]
  nawActief: boolean
  nawVertragingMin: number
  nawSoorten: readonly VoertuigMutatieSoort[]
  tijdelijkNawDrempelDagen: number
  terugwerkendMarkeringDagen: number
  terugwerkendAkkoordDagen: number
}

export interface KanaalBesluit {
  kanaal: VoertuigMeldingKanaal
  actie: 'MELDEN' | 'OVERSLAAN'
  /** Vaste code (filterbaar) plus een leesbare reden; null bij MELDEN zonder bijzonderheid. */
  redenCode: string | null
  reden: string | null
  /** Vraagt dit besluit een signaal aan de doelgroep (fout of akkoord nodig)? */
  signaal: boolean
  /** Ingangsdatum langer terug dan de markeringsdrempel. */
  terugwerkend: boolean
  /** Ingangsdatum langer terug dan de akkoorddrempel: pas na akkoord versturen. */
  vereistAkkoord: boolean
  geplandOp: Date
  /** `voertuig:<id>` (WASSINK, CARTRACKER) of `medewerker:<id>` (NAW). */
  bundelSleutel: string
}

export const BESTUURDER_SOORTEN: readonly VoertuigMutatieSoort[] = [
  'BESTUURDER_TOEGEWEZEN',
  'BESTUURDER_GEWISSELD',
  'BESTUURDER_INGETROKKEN',
  'TIJDELIJK_GESTART',
  'TIJDELIJK_BEEINDIGD',
]

const LEASE_EIGENDOM: readonly VoertuigEigendomSoort[] = ['OPERATIONAL_LEASE', 'FINANCIAL_LEASE']

/**
 * Voertuigtypen die als auto van de zaak op de loonstrook kunnen staan
 * (bijtelling of een verklaring geen privégebruik). Een vrachtwagen, aanhanger,
 * heftruck of hoogwerker niet. OVERIG telt mee: bij twijfel melden, niet stil.
 */
export const AUTO_VAN_DE_ZAAK_TYPES: readonly VoertuigType[] = ['PERSONENAUTO', 'BESTELBUS', 'OVERIG']

export function isBestuurderSoort(soort: VoertuigMutatieSoort): boolean {
  return BESTUURDER_SOORTEN.includes(soort)
}

export interface Relevantie {
  relevant: boolean
  redenCode: string | null
  reden: string | null
}

const RELEVANT: Relevantie = { relevant: true, redenCode: null, reden: null }

function niet(redenCode: string, reden: string): Relevantie {
  return { relevant: false, redenCode, reden }
}

/**
 * Verandert de loonstrook door dit event? (spec 227 §A2 + §D9 + A3)
 *
 * Bestuurder start/einde/wissel op een auto van de zaak, een tijdelijke
 * bestuurder langer dan de drempel (14 dagen), een gewijzigde bijtelling of
 * verklaring, en uit-vloot met een bestuurder. De bijtellingscampagne mailt de
 * salarisverwerker zelf (FO-501): die verklaringen gaan hier niet nog een keer.
 */
export function isNawRelevant(
  event: VoertuigMeldingEventInvoer,
  voertuig: Pick<VoertuigMeldingVoertuigInvoer, 'type'>,
  regels: Pick<VoertuigMeldingRegels, 'nawSoorten' | 'tijdelijkNawDrempelDagen'>,
): Relevantie {
  if (!regels.nawSoorten.includes(event.soort)) {
    return niet('NAW_REGEL_UIT', 'Deze soort mutatie staat niet in de NAW-regels (instellingen).')
  }
  if (!event.employeeId) {
    return niet('GEEN_BESTUURDER', 'Er is geen medewerker bij betrokken: de loonstrook verandert niet.')
  }
  if (!AUTO_VAN_DE_ZAAK_TYPES.includes(voertuig.type)) {
    return niet('GEEN_AUTO_VAN_DE_ZAAK', 'Dit voertuigtype staat niet als auto van de zaak op de loonstrook.')
  }
  const drempel = regels.tijdelijkNawDrempelDagen
  switch (event.soort) {
    case 'TIJDELIJK_GESTART': {
      if (!event.tijdelijkVan || !event.tijdelijkTot) {
        return niet(
          'TIJDELIJK_KORT',
          `Tijdelijke bestuurder zonder verwachte einddatum: de salarisadministratie volgt zodra de periode langer duurt dan ${drempel} dagen.`,
        )
      }
      if (dagenTussen(event.tijdelijkVan, event.tijdelijkTot) <= drempel) {
        return niet('TIJDELIJK_KORT', `Tijdelijke bestuurder voor ${drempel} dagen of korter: geen bijtellingsgevolg (A3).`)
      }
      return RELEVANT
    }
    case 'TIJDELIJK_BEEINDIGD': {
      if (event.tijdelijkStartGemeld) return RELEVANT
      if (event.tijdelijkVan && event.tijdelijkTot && dagenTussen(event.tijdelijkVan, event.tijdelijkTot) > drempel) {
        return RELEVANT
      }
      return niet('TIJDELIJK_KORT', `Tijdelijke bestuurder voor ${drempel} dagen of korter: de start is ook niet gemeld (A3).`)
    }
    case 'VERKLARING_VASTGELEGD': {
      if (event.bron === 'CAMPAGNE') {
        return niet('CAMPAGNE', 'De bijtellingscampagne stuurt de correctie zelf naar de salarisverwerker.')
      }
      if (event.bijtellingVoorheen != null && event.bijtellingNu === event.bijtellingVoorheen) {
        return niet('GEEN_WIJZIGING', 'Herbevestiging: de bijtelling verandert niet.')
      }
      return RELEVANT
    }
    case 'VERKLARING_INGETROKKEN': {
      if (event.intrekReden === 'WISSEL') {
        return niet('GEVOLG_WISSEL', 'Gevolg van de autowissel: de wissel zelf is al gemeld.')
      }
      if (event.intrekReden === 'VERVANGEN') {
        return niet('VERVANGEN', 'Vervangen door een nieuwe verklaring: die wordt gemeld.')
      }
      if (event.verklaringType !== 'UITSLUITEND_ZAKELIJK') {
        return niet('GEEN_WIJZIGING', 'De ingetrokken verklaring had bijtelling: de loonstrook verandert niet.')
      }
      return RELEVANT
    }
    default:
      return RELEVANT
  }
}

function leaseRelevant(event: VoertuigMeldingEventInvoer): Relevantie {
  switch (event.soort) {
    case 'VOERTUIG_IN_VLOOT':
      return niet('NIET_RELEVANT', 'Een nieuw voertuig in de vloot is al bekend bij de leasemaatschappij.')
    case 'VERKLARING_VASTGELEGD':
      if (event.bijtellingVoorheen != null && event.bijtellingNu === event.bijtellingVoorheen) {
        return niet('GEEN_WIJZIGING', 'Herbevestiging: het privégebruik verandert niet.')
      }
      return RELEVANT
    case 'VERKLARING_INGETROKKEN':
      if (event.intrekReden === 'WISSEL') return niet('GEVOLG_WISSEL', 'Gevolg van de autowissel: de wissel zelf is al gemeld.')
      if (event.intrekReden === 'VERVANGEN') return niet('VERVANGEN', 'Vervangen door een nieuwe verklaring: die wordt gemeld.')
      return RELEVANT
    default:
      return RELEVANT
  }
}

const CARTRACKER_SOORTEN: readonly VoertuigMutatieSoort[] = [...BESTUURDER_SOORTEN, 'VOERTUIG_UIT_VLOOT', 'KENTEKEN_GEWIJZIGD']

const KANAAL_UIT_REDEN: Record<VoertuigMeldingKanaal, string> = {
  WASSINK: 'Het kanaal leasemaatschappij staat uit (instellingen).',
  CARTRACKER: 'Het kanaal Cartracker staat uit (instellingen).',
  NAW: 'Het kanaal salarisadministratie staat uit (instellingen).',
}

/**
 * Een uitgezet kanaal (integrator-review H1): alleen een besluit dat anders MELDEN
 * zou zijn, wordt OVERGESLAGEN `KANAAL_UIT`. Een besluit met een eigen reden (geen
 * lease, andere leasemaatschappij, geen tracker, herbevestiging, historie-import)
 * houdt die reden, en een signaal (leasemaatschappij onbekend) blijft staan. Zo
 * beslist "opnieuw" na het aanzetten alleen over wat echt gemeld moest worden.
 */
export function pasKanaalUitToe(b: KanaalBesluit, actief: boolean): KanaalBesluit {
  if (actief || b.actie !== 'MELDEN') return b
  return {
    ...b,
    actie: 'OVERSLAAN',
    redenCode: 'KANAAL_UIT',
    reden: KANAAL_UIT_REDEN[b.kanaal],
    signaal: false,
    vereistAkkoord: false,
  }
}

function besluit(
  kanaal: VoertuigMeldingKanaal,
  bundelSleutel: string,
  geplandOp: Date,
  basis: Partial<Omit<KanaalBesluit, 'kanaal' | 'bundelSleutel' | 'geplandOp'>> & Pick<KanaalBesluit, 'actie'>,
): KanaalBesluit {
  return {
    kanaal,
    bundelSleutel,
    geplandOp,
    redenCode: null,
    reden: null,
    signaal: false,
    terugwerkend: false,
    vereistAkkoord: false,
    ...basis,
  }
}

function terugwerkendheid(
  ingangsdatum: Date,
  nu: Date,
  regels: Pick<VoertuigMeldingRegels, 'terugwerkendMarkeringDagen' | 'terugwerkendAkkoordDagen'>,
): { terugwerkend: boolean; vereistAkkoord: boolean } {
  const dagenTerug = dagenTussen(ingangsdatum, nu)
  return {
    terugwerkend: dagenTerug > regels.terugwerkendMarkeringDagen,
    vereistAkkoord: dagenTerug > regels.terugwerkendAkkoordDagen,
  }
}

/** Geplande verzendtijd: het event plus de bundelvertraging van het kanaal. */
export function geplandVoor(createdAt: Date, vertragingMin: number): Date {
  return new Date(createdAt.getTime() + Math.max(0, vertragingMin) * MINUUT_MS)
}

/**
 * Per kanaal precies één besluit, in vaste volgorde WASSINK, CARTRACKER, NAW
 * (spec 227 §A2). Zie de kop van dit bestand.
 */
export function bepaalVoertuigMeldingen(
  event: VoertuigMeldingEventInvoer,
  voertuig: VoertuigMeldingVoertuigInvoer,
  regels: VoertuigMeldingRegels,
  nu: Date,
): [KanaalBesluit, KanaalBesluit, KanaalBesluit] {
  const tw = terugwerkendheid(event.ingangsdatum, nu, regels)
  const voertuigSleutel = `voertuig:${event.voertuigId}`
  const meldenBasis = (redenCode: string | null, reden: string | null) => ({
    actie: 'MELDEN' as const,
    redenCode: tw.vereistAkkoord ? 'AKKOORD_NODIG' : (redenCode ?? (tw.terugwerkend ? 'TERUGWERKEND' : null)),
    reden: tw.vereistAkkoord
      ? `De ingangsdatum ligt meer dan ${regels.terugwerkendAkkoordDagen} dagen terug: versturen na akkoord van wagenparkbeheer.`
      : (reden ?? (tw.terugwerkend ? `Met terugwerkende kracht (meer dan ${regels.terugwerkendMarkeringDagen} dagen).` : null)),
    signaal: tw.vereistAkkoord,
    terugwerkend: tw.terugwerkend,
    vereistAkkoord: tw.vereistAkkoord,
  })

  // Een import van historie (bestuurder sinds lang geleden) is geen mutatie om te
  // melden: zonder deze regel gaf elke geïmporteerde auto een akkoordvraag (review L3).
  // Niet stil: OVERGESLAGEN met reden, zichtbaar in het overzicht.
  const historieImport = event.bron === 'IMPORT' && tw.vereistAkkoord
  const melden = (redenCode: string | null = null, reden: string | null = null) =>
    historieImport
      ? {
          actie: 'OVERSLAAN' as const,
          redenCode: 'HISTORIE_IMPORT',
          reden: `Historie uit een import: de mutatie ligt meer dan ${regels.terugwerkendAkkoordDagen} dagen terug en wordt niet gemeld.`,
          terugwerkend: true,
        }
      : meldenBasis(redenCode, reden)

  // ── Leasemaatschappij (Wassink)
  const wassinkGepland = geplandVoor(event.createdAt, regels.wassinkVertragingMin)
  let wassink: KanaalBesluit
  if (!LEASE_EIGENDOM.includes(voertuig.eigendom)) {
    wassink = besluit('WASSINK', voertuigSleutel, wassinkGepland, {
      actie: 'OVERSLAAN',
      redenCode: 'GEEN_LEASE',
      reden: 'Geen leasevoertuig.',
    })
  } else if (!voertuig.leaseMaatschappijId) {
    wassink = besluit('WASSINK', voertuigSleutel, wassinkGepland, {
      actie: 'OVERSLAAN',
      redenCode: 'LEASEMAATSCHAPPIJ_ONBEKEND',
      reden: 'Leasemaatschappij onbekend: koppel de leasemaatschappij op het voertuig.',
      signaal: true,
    })
  } else if (!regels.wassinkLeveranciers.includes(voertuig.leaseMaatschappijId)) {
    wassink = besluit('WASSINK', voertuigSleutel, wassinkGepland, {
      actie: 'OVERSLAAN',
      redenCode: 'LEVERANCIER_ONTVANGT_NIET',
      reden: `${voertuig.leaseMaatschappijNaam ?? 'Deze leasemaatschappij'} ontvangt geen bestuurdersmutaties (instellingen).`,
    })
  } else {
    const r = leaseRelevant(event)
    wassink = r.relevant
      ? besluit('WASSINK', voertuigSleutel, wassinkGepland, melden())
      : besluit('WASSINK', voertuigSleutel, wassinkGepland, { actie: 'OVERSLAAN', redenCode: r.redenCode, reden: r.reden })
  }

  // ── Cartracker
  const cartrackerGepland = geplandVoor(event.createdAt, regels.cartrackerVertragingMin)
  let cartracker: KanaalBesluit
  if (!regels.cartrackerVoertuigTypes.includes(voertuig.type)) {
    cartracker = besluit('CARTRACKER', voertuigSleutel, cartrackerGepland, {
      actie: 'OVERSLAAN',
      redenCode: 'GEEN_TRACKER',
      reden: 'Dit type voertuig heeft geen tracker (instellingen).',
    })
  } else if (!CARTRACKER_SOORTEN.includes(event.soort)) {
    cartracker = besluit('CARTRACKER', voertuigSleutel, cartrackerGepland, {
      actie: 'OVERSLAAN',
      redenCode: 'NIET_RELEVANT',
      reden: 'Cartracker kent alleen de bestuurder en het kenteken.',
    })
  } else {
    cartracker = besluit('CARTRACKER', voertuigSleutel, cartrackerGepland, melden())
  }

  // ── Salarisverwerker (NAW): per medewerker gebundeld.
  const nawGepland = geplandVoor(event.createdAt, regels.nawVertragingMin)
  const nawSleutel = event.employeeId ? `medewerker:${event.employeeId}` : voertuigSleutel
  const nawRelevant = isNawRelevant(event, voertuig, regels)
  const naw: KanaalBesluit = nawRelevant.relevant
    ? besluit('NAW', nawSleutel, nawGepland, melden())
    : besluit('NAW', nawSleutel, nawGepland, { actie: 'OVERSLAAN', redenCode: nawRelevant.redenCode, reden: nawRelevant.reden })

  return [
    pasKanaalUitToe(wassink, regels.wassinkActief),
    pasKanaalUitToe(cartracker, regels.cartrackerActief),
    pasKanaalUitToe(naw, regels.nawActief),
  ]
}

// ─── Bundelen ─────────────────────────────────────────────────────────────────

/**
 * Bundel events op één bundel-sleutel: een bundel begint bij het oudste nog niet
 * gebundelde event en neemt alles mee dat binnen `vensterMs` daarna ontstond
 * (vast venster vanaf het eerste event, geen glijdend venster: een voertuig met
 * aanhoudende wijzigingen schuift de melding zo nooit eindeloos op).
 */
export function bundelEvents<T extends { createdAt: Date }>(events: readonly T[], vensterMs: number): T[][] {
  const gesorteerd = [...events].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
  const bundels: T[][] = []
  let huidig: T[] = []
  let start = 0
  for (const e of gesorteerd) {
    const t = e.createdAt.getTime()
    if (huidig.length === 0) {
      huidig = [e]
      start = t
      continue
    }
    if (t - start < vensterMs) {
      huidig.push(e)
    } else {
      bundels.push(huidig)
      huidig = [e]
      start = t
    }
  }
  if (huidig.length > 0) bundels.push(huidig)
  return bundels
}

/** Een event zoals het in een bundel meetelt (gezien vanuit voertuig of medewerker). */
export interface BundelEventInvoer {
  soort: VoertuigMutatieSoort
  createdAt: Date
  ingangsdatum: Date
  voertuigId: string
  employeeId: string | null
  /** GEWISSELD: de vorige bestuurder van het voertuig. */
  vorigeEmployeeId?: string | null
  /** Verklaring: verandert de bijtelling (false bij een herbevestiging)? */
  wijzigtBijtelling?: boolean
}

export interface BundelUitkomst {
  /** De mutaties heffen elkaar op (bv. toegewezen en direct weer afgemeld). */
  heftOp: boolean
  /** Voertuig-bundel: de bestuurder voor en na de bundel. */
  bestuurderVoor: string | null
  bestuurderNa: string | null
  /** De soort die de melding draagt (wissel, start, einde of de laatste overige). */
  hoofdSoort: VoertuigMutatieSoort
  /**
   * De ingangsdatum van de melding: die van de laatste bestuurderswijziging als die
   * erin zit (een wissel met een verklaring die pas per 1 januari geldt, blijft "per
   * de wisseldatum"; review L7), anders die van het laatste event.
   */
  ingangsdatum: Date
  soorten: VoertuigMutatieSoort[]
}

const START_SOORTEN: readonly VoertuigMutatieSoort[] = ['BESTUURDER_TOEGEWEZEN', 'BESTUURDER_GEWISSELD', 'TIJDELIJK_GESTART']
const EINDE_SOORTEN: readonly VoertuigMutatieSoort[] = ['BESTUURDER_INGETROKKEN', 'TIJDELIJK_BEEINDIGD']

/**
 * De eindstand van een bundel. `perspectief` VOERTUIG kijkt naar de bestuurder
 * van het voertuig (Wassink, Cartracker); MEDEWERKER naar welke voertuigen de
 * medewerker heeft (NAW). Heffen de bestuurder-events elkaar op en zit er geen
 * andere wijziging in de bundel, dan is er niets te melden.
 */
export function bundelUitkomst(
  events: readonly BundelEventInvoer[],
  perspectief: 'VOERTUIG' | 'MEDEWERKER',
): BundelUitkomst {
  if (events.length === 0) throw new Error('bundelUitkomst: lege bundel')
  const gesorteerd = [...events].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
  const laatste = gesorteerd[gesorteerd.length - 1]!
  const soorten = gesorteerd.map((e) => e.soort)
  const anders = gesorteerd.filter(
    (e) => !isBestuurderSoort(e.soort) && !(e.soort === 'VERKLARING_VASTGELEGD' && e.wijzigtBijtelling === false),
  )
  const bestuurderEvents = gesorteerd.filter((e) => isBestuurderSoort(e.soort))

  let bestuurderVoor: string | null = null
  let bestuurderNa: string | null = null
  let opgeheven = false
  if (perspectief === 'VOERTUIG' && bestuurderEvents.length > 0) {
    const eerste = bestuurderEvents[0]!
    bestuurderVoor = EINDE_SOORTEN.includes(eerste.soort)
      ? eerste.employeeId
      : eerste.soort === 'BESTUURDER_GEWISSELD'
        ? (eerste.vorigeEmployeeId ?? null)
        : null
    const l = bestuurderEvents[bestuurderEvents.length - 1]!
    bestuurderNa = START_SOORTEN.includes(l.soort) ? l.employeeId : null
    opgeheven = bestuurderVoor === bestuurderNa
  }
  if (perspectief === 'MEDEWERKER' && bestuurderEvents.length > 0) {
    // Per voertuig: had de medewerker hem voor de bundel, en heeft hij hem erna?
    const stand = new Map<string, { voor: boolean; na: boolean }>()
    for (const e of bestuurderEvents) {
      const s = stand.get(e.voertuigId) ?? { voor: EINDE_SOORTEN.includes(e.soort), na: false }
      s.na = START_SOORTEN.includes(e.soort)
      stand.set(e.voertuigId, s)
    }
    opgeheven = [...stand.values()].every((s) => s.voor === s.na)
  }
  const heftOp = bestuurderEvents.length > 0 && anders.length === 0 && opgeheven
    ? true
    : bestuurderEvents.length === 0 && anders.length === 0

  let hoofdSoort: VoertuigMutatieSoort = laatste.soort
  if (soorten.includes('VOERTUIG_UIT_VLOOT')) {
    // Uit de vloot draagt de melding: de afmelding van de bestuurder hoort erbij.
    hoofdSoort = 'VOERTUIG_UIT_VLOOT'
  } else if (perspectief === 'VOERTUIG' && bestuurderEvents.length > 0 && !opgeheven) {
    const l = bestuurderEvents[bestuurderEvents.length - 1]!
    if (bestuurderVoor && bestuurderNa) hoofdSoort = l.soort === 'TIJDELIJK_GESTART' ? 'TIJDELIJK_GESTART' : 'BESTUURDER_GEWISSELD'
    else hoofdSoort = l.soort
  } else if (perspectief === 'MEDEWERKER' && bestuurderEvents.length > 0 && !opgeheven) {
    const starts = bestuurderEvents.filter((e) => START_SOORTEN.includes(e.soort))
    const eindes = bestuurderEvents.filter((e) => EINDE_SOORTEN.includes(e.soort))
    if (starts.length > 0 && eindes.length > 0) {
      hoofdSoort = starts.some((e) => e.soort === 'TIJDELIJK_GESTART') && !starts.some((e) => e.soort !== 'TIJDELIJK_GESTART')
        ? 'TIJDELIJK_GESTART'
        : 'BESTUURDER_GEWISSELD'
    } else {
      hoofdSoort = bestuurderEvents[bestuurderEvents.length - 1]!.soort
    }
  } else if (anders.length > 0) {
    hoofdSoort = anders[anders.length - 1]!.soort
  }

  const draagt = bestuurderEvents.length > 0 ? bestuurderEvents[bestuurderEvents.length - 1]! : laatste
  return {
    heftOp,
    bestuurderVoor,
    bestuurderNa,
    hoofdSoort,
    ingangsdatum: draagt.ingangsdatum,
    soorten,
  }
}

/** Is dit de start van een bestuurder (toegewezen, gewisseld, tijdelijk)? */
export function isBestuurderStart(soort: VoertuigMutatieSoort): boolean {
  return START_SOORTEN.includes(soort)
}

/**
 * Overgang van de oude bestuurderswisselmail (tot batch-70; review B1): meldde die
 * deze bundel al? Alleen als de bundel uit niets anders bestaat dan starts die de
 * oude mail meldde (die mailde per NIEUWE toewijzing, met de vorige bestuurder
 * erbij) en de afmelding van de vorige bestuurder daarvoor. Een verklaring, een
 * inname na de start of een start die de oude mail niet kende, wordt gewoon gemeld.
 */
export function isAlGemeldDoorOudeMail(
  events: ReadonlyArray<{ soort: string; toewijzingId: string | null; createdAt: Date }>,
  legacyToewijzingIds: ReadonlySet<string>,
): boolean {
  const isStart = (s: string) => (START_SOORTEN as readonly string[]).includes(s)
  const starts = events.filter((e) => isStart(e.soort))
  if (starts.length === 0) return false
  if (!starts.every((e) => e.toewijzingId !== null && legacyToewijzingIds.has(e.toewijzingId))) return false
  const laatsteStart = Math.max(...starts.map((e) => e.createdAt.getTime()))
  return events
    .filter((e) => !isStart(e.soort))
    .every((e) => (EINDE_SOORTEN as readonly string[]).includes(e.soort) && e.createdAt.getTime() <= laatsteStart)
}

// ─── Onderwerpen en antwoorden ───────────────────────────────────────────────

const NL_DATUM = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** dd-mm-jjjj in Europe/Amsterdam. */
export function nlMeldingDatum(d: Date): string {
  return NL_DATUM.format(d)
}

/** Onderwerp van de mail aan de leasemaatschappij (spec 227 §B5). */
export function leaseMeldingOnderwerp(kenteken: string, datum: string): string {
  return `STREEP - Bestuurdersmutatie ${kenteken} per ${datum}`
}

/** Onderwerp van de mail aan de salarisverwerker (spec 227 §D9). Nooit een BSN. */
export function salarisMeldingOnderwerp(personeelsnummer: string | null, naam: string, datum: string): string {
  return `STREEP - Loonstrookcorrectie auto ${personeelsnummer ?? 'zonder personeelsnummer'} ${naam} per ${datum}`
}

/** Onderwerp zonder antwoord- of doorstuurvoorvoegsels, kleine letters, enkele spaties. */
export function normaliseerOnderwerp(onderwerp: string): string {
  let s = onderwerp.trim().toLowerCase().replace(/\s+/g, ' ')
  // RE:, Re[2]:, AW:, Antw:, Antwoord:, FW:, Fwd:, Doorst:, WG: (meerdere keren mogelijk)
  const voorvoegsel = /^(re|aw|antw|antwoord|fw|fwd|doorst|wg|sv|vs)(\[\d+\])?\s*:\s*/
  while (voorvoegsel.test(s)) s = s.replace(voorvoegsel, '')
  return s
}

/**
 * Is `antwoord` een antwoord op de mail met onderwerp `verstuurd`? Het hele
 * onderwerp (zonder voorvoegsels) moet erin staan: kenteken of personeelsnummer
 * én datum, zodat een antwoord op een andere mutatie nooit de verkeerde opdracht
 * bevestigt.
 */
export function isAntwoordOp(antwoord: string, verstuurd: string): boolean {
  const a = normaliseerOnderwerp(antwoord)
  const v = normaliseerOnderwerp(verstuurd)
  return v.length > 0 && a.includes(v)
}

// Een gateway-tag ervoor ("[EXTERN] RE: ...") mag (review ronde 2).
const ANTWOORD_VOORVOEGSEL = /^\s*(\[[^\]]{1,30}\]\s*)*(re|aw|antw|antwoord|sv|vs)(\[\d+\])?\s*:/i

/**
 * Begint het onderwerp met een antwoord-voorvoegsel (RE:, AW:, Antw:)? Een kopie
 * van de eigen mail, een doorgestuurde mail of een nieuwe mail met hetzelfde
 * onderwerp is geen antwoord (review H1).
 */
export function heeftAntwoordVoorvoegsel(onderwerp: string): boolean {
  return ANTWOORD_VOORVOEGSEL.test(onderwerp)
}

/** De datum uit een melding-onderwerp ("... per dd-mm-jjjj"), of null. */
export function datumUitOnderwerp(onderwerp: string): string | null {
  const m = /per (\d{2})-(\d{2})-(\d{4})/.exec(normaliseerOnderwerp(onderwerp))
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null
}

/**
 * Bevestigingstermijn: is de melding langer dan `werkdagen` werkdagen
 * onbevestigd? (ma-vr, geen feestdag; spec 227 §B5/§D10: 5 werkdagen).
 */
export function isBevestigingVerlopen(input: {
  verstuurdOpYmd: string
  vandaagYmd: string
  werkdagen: number
  feestdagenYmd?: ReadonlySet<string>
}): boolean {
  return werkdagenSinds(input.verstuurdOpYmd, input.vandaagYmd, input.feestdagenYmd ?? new Set()) >= input.werkdagen
}
